// After a deploy the open tab still holds the old index.html, whose lazy chunks (hashed file names)
// are gone from the server: the next lazy screen fails with "Failed to fetch dynamically imported
// module". Reloading once picks up the new build. A short guard prevents a reload loop when the
// chunk is missing for another reason (offline, broken deploy).

const RELOAD_KEY = "xentros.staleChunkReloadAt";
const RELOAD_GUARD_MS = 60_000;

const STALE_CHUNK_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
  /Unable to preload CSS/i,
  /ChunkLoadError/i,
];

export function isStaleChunkError(error: unknown): boolean {
  const message = error instanceof Error ? `${error.name}: ${error.message}` : String(error ?? "");
  return STALE_CHUNK_PATTERNS.some((pattern) => pattern.test(message));
}

/** Reloads the page once per guard window; returns true when a reload was started. */
export function reloadOnceForStaleChunk(storage: Pick<Storage, "getItem" | "setItem"> | null = safeSessionStorage(), now = Date.now()): boolean {
  try {
    const last = Number(storage?.getItem(RELOAD_KEY) ?? 0);
    if (Number.isFinite(last) && now - last < RELOAD_GUARD_MS) return false;
    storage?.setItem(RELOAD_KEY, String(now));
  } catch {
    // Storage blocked: still reload once; the browser's own reload stops a tight loop.
  }
  window.location.reload();
  return true;
}

/** Vite fires vite:preloadError when a lazy chunk or its CSS cannot be loaded. */
export function installStaleChunkReload() {
  window.addEventListener("vite:preloadError", (event) => {
    if (reloadOnceForStaleChunk()) event.preventDefault();
  });
}

function safeSessionStorage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}
